import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, MessageCircle } from "lucide-react";

const ease = [0.25, 1, 0.5, 1];

const HIGHLIGHTS = [
  "Driveways & extensions",
  "Patios & backyard slabs",
  "Walkways & sidewalks",
  "Shed & garage pads",
  "Curbing & step formwork",
  "Broom, smooth, or stamped finishes",
];

const INCLUDES = [
  "Free on-site consultation & measurement",
  "Site prep, forming, and grading",
  "Rebar or wire mesh reinforcement as needed",
  "Ready-mix concrete pour",
  "Hand-troweled and edged finish",
  "Control joints cut for crack prevention",
  "Site cleanup and haul-away",
  "Cure guidance so your slab hardens right",
];

const FAQ = [
  {
    q: "How long before I can walk on new concrete?",
    a: "Foot traffic is safe after 24 hours. Wait 5–7 days before heavy use like driveways.",
  },
  {
    q: "How long does a pour typically take?",
    a: "Most residential pads are prepped and poured in 1–2 days, weather permitting.",
  },
  {
    q: "Do you handle removal of old concrete?",
    a: "Yes — we can break up, haul away, and prep the area before pouring new concrete.",
  },
  {
    q: "What finishes do you offer?",
    a: "Broom (most common, non-slip), smooth trowel, and stamped/decorative on request.",
  },
];

export default function ConcretePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F8F2] text-[#122412]">
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="back-home" className="flex items-center gap-3">
            <img
              src="/brand/yair-logo.jpg"
              alt="Yair Landscaping"
              className="h-12 w-auto rounded-md object-contain"
            />
            <span className="font-serif text-xl md:text-2xl tracking-tight text-[#F4F8F2]">
              Yair <span className="italic font-light">Landscaping</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 overline text-[#F4F8F2] hover:text-[#7BBADB] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[70vh] w-full overflow-hidden bg-[#1B3A1B]">
        <img
          src="/services/concrete.webp"
          alt="Freshly poured concrete pad by Yair Landscaping"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A1B] via-[#1B3A1B]/55 to-[#1B3A1B]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-[70vh] flex flex-col justify-end pb-16 lg:pb-24 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="overline text-[#7BBADB] mb-6"
          >
            Service · Birmingham, AL
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.15 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F4F8F2] font-light leading-[0.95] tracking-tight max-w-4xl"
          >
            Concrete
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.35 }}
            className="font-body mt-8 text-[#DDECD7] max-w-2xl text-lg leading-relaxed"
          >
            Driveways, patios, walkways, and slabs — poured clean, cured strong,
            and built to last decades.
          </motion.p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="overline text-[#1F70BF] mb-6">What we pour</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#122412] font-light leading-[1.05] tracking-tight">
                Solid work,
                <br />
                <span className="italic">every time.</span>
              </h2>
            </div>
            <div className="md:col-span-7 font-body text-[#4A6B4A] text-lg leading-relaxed space-y-6">
              <p>
                Concrete is a permanent addition to your property — the prep
                work matters as much as the pour. We handle every step so your
                slab stays flat, level, and crack-resistant for years.
              </p>
              <ul className="space-y-3">
                {HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#2E7D32] mt-1 shrink-0" strokeWidth={1.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="py-20 lg:py-28 bg-[#DDECD7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="overline text-[#1F70BF] mb-6">Recent pour</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight max-w-3xl">
              Clean lines,
              <br />
              <span className="italic">smooth finish.</span>
            </h2>
          </div>
          <div className="relative w-full overflow-hidden border border-[#C5D8B8] bg-[#1B3A1B]">
            <img
              src="/services/concrete-ba.webp"
              alt="Concrete before and after — trench prep to finished pad"
              className="w-full h-auto block"
              data-testid="concrete-showcase"
            />
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="py-20 lg:py-28 bg-[#1B3A1B] text-[#F4F8F2]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="overline text-[#7BBADB] mb-6">What's included</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#F4F8F2] font-light leading-[1.05] tracking-tight max-w-3xl mb-12">
            From prep
            <br />
            <span className="italic text-[#DDECD7]">to final cure.</span>
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {INCLUDES.map((item) => (
              <li key={item} className="flex items-start gap-3 font-body">
                <CheckCircle2 className="w-5 h-5 text-[#7BBADB] mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-[#DDECD7] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="overline text-[#1F70BF] mb-6">Common questions</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight">
              Good to know.
            </h2>
          </div>
          <div className="space-y-6">
            {FAQ.map((f, i) => (
              <div key={f.q} className="border-t border-[#C5D8B8] pt-6" data-testid={`faq-${i}`}>
                <h3 className="font-serif text-xl md:text-2xl text-[#122412] font-light mb-3">
                  {f.q}
                </h3>
                <p className="font-body text-[#4A6B4A] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#DDECD7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="overline text-[#1F70BF] mb-6">Ready to start?</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight mb-6">
            Let's pour something
            <br />
            <span className="italic">built to last.</span>
          </h2>
          <p className="font-body text-[#4A6B4A] text-lg leading-relaxed max-w-xl mx-auto mb-10">
            Free, no-obligation consultation. Most quotes returned within 1–2 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#contact"
              data-testid="cta-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#2E7D32] text-[#F4F8F2] px-10 py-5 overline hover:bg-[#388E3C] transition-colors"
            >
              Request a Free Quote
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href="tel:+12052033712"
              data-testid="cta-call"
              className="inline-flex items-center justify-center gap-3 border border-[#2E7D32] text-[#2E7D32] px-10 py-5 overline hover:bg-[#2E7D32] hover:text-[#F4F8F2] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} /> (205) 203-3712
            </a>
            <a
              href="sms:+12052033712"
              data-testid="cta-text"
              className="inline-flex items-center justify-center gap-3 border border-[#1F70BF] text-[#1F70BF] px-10 py-5 overline hover:bg-[#1F70BF] hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Text Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B3A1B] text-[#DDECD7] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="overline hover:text-[#7BBADB]">
            ← Back to Yair Landscaping
          </Link>
          <span className="overline">© {new Date().getFullYear()} Yair Landscaping</span>
        </div>
      </footer>
    </div>
  );
}
